import { z } from 'zod';
import { digestSchema } from '../hashing/hash.js';
import { personIdSchema } from './person.js';
import { fileCategorySchema, isSegregatedCategory } from '../files/categories.js';
import { fileStoreBlockedCodeSchema, fileStoreCodeSchema, fileStoreParkedCodeSchema } from '../files/store-plan.js';
import { lifecycleAuditDetailSchema } from './status-reasons.js';
import { sensitiveFieldSchema, sensitiveRevealAuditDetailsSchema } from '../sensitive/vocabulary.js';
/**
 * AuditEvent — every state change, email send, token event, file commit and file view or
 * download writes one (CLAUDE.md domain rules; ADR-009 §5). Append-only: nothing updates or deletes an audit document.
 *
 * Stored in the `audit` container, partitioned on /personId, so a survey question about
 * one person is a single-partition read.
 *
 * `personId` is required and the container is partitioned on it, so there is nowhere to
 * put an event that belongs to nobody. Anonymous activity — a failed token attempt from an
 * unknown visitor, a health probe — is Application Insights TELEMETRY, not an AuditEvent.
 * Agency-level events use AGENCY_SCOPE_PERSON_ID.
 *
 * NO PII IN THE PAYLOAD. The event says what changed and who changed it; the person's
 * details live on the person record, which the audit row points at. An audit trail that
 * duplicates contact details becomes a second copy of the personnel file with none of its
 * access controls.
 */
export const auditActionSchema = z.enum([
    'person.created',
    'person.updated',
    'person.history-recorded',
    'person.addendum-recorded',
    'person.lifecycle-changed',
    /** ADR-012 §5: field names and purpose codes only, never a value — see `auditEventSchema`. */
    'person.sensitive-updated',
    'person.sensitive-revealed',
    /**
     * A reveal the hourly limit refused (#371, ADR-012 §4).
     *
     * WHY IT EXISTS. `apps/api/src/sensitive/` may log literal strings only — `check:sensitive-fields`
     * enforces it, and that rule is why a Social Security number cannot reach a log by accident. So
     * the alert can say a limit was hit and nothing else: not who, not whose record, not which field.
     * Attribution has to live somewhere a responder can query, and this is it.
     *
     * It also settles an inconsistency. There are two refusal paths — one before the reservation row
     * and one after — and they left different trails for the same user action, so what a responder
     * saw depended on timing. Both now write this.
     *
     * SAME GUARANTEES AS A REVEAL: the field and the purpose, no free text, no changed fields, and
     * never a value. It is partitioned by the SUBJECT's `personId` like every other row — the actor
     * is already named by `actorId`, and "who has been looking at this person" is the question a
     * responder actually asks.
     */
    'person.sensitive-reveal-refused',
    'requirement.exception-granted',
    'requirement.exception-revoked',
    // Phase 2. Widened once, up front, so twelve later PRs do not each edit this union.
    'step.issued',
    'step.sent',
    'step.started',
    /**
     * The person worked on a form during one portal session (ADR-013 §5). ONE row per form per
     * session, whatever the number of autosaves: its id is derived from the instance and the
     * session and it is created only if absent. It names no field and copies no value, because a
     * draft is a scratchpad, not a record.
     */
    'step.draft-saved',
    /**
     * HR opened the answers of a form whose file category is SEGREGATED — health or I-9 (ADR-013
     * §4). Written BEFORE the response, like the ADR-009 file view it mirrors: a Hep B declination
     * sitting in Cosmos is as much a medical record as the scanned waiver, and reading one should
     * leave the same trace.
     *
     * Ordinary answers are NOT audited. Reading a person's record is not an event, and a row per
     * ordinary form view would bury the restricted ones it exists to make visible.
     */
    'step.answers-viewed',
    /**
     * A view of a segregated form that was REFUSED (#385, ADR-013 §4).
     *
     * Without it, repeated attempts to read somebody's health answers by a caller who cannot are
     * invisible, while every successful one is recorded. Shaped like `file.access-refused` (#184):
     * it names the instance that was asked for as its subject and carries NO `answers` block, because
     * that block says `segregated: true` and would tell anyone reading the trail that a segregated
     * form exists — or that the id was real at all.
     */
    'step.answers-refused',
    'step.submitted',
    /**
     * A person signed this step (ADR-014 §3, #215). IN ADDITION to `step.submitted`, never instead
     * of it, and carrying only the `SignatureEvidence` id in `reason` — the IP address and user
     * agent live on that record, because an audit row carries no PII by rule.
     */
    'step.signed',
    'step.approved',
    'step.rejected',
    'step.cancelled',
    'definition.published',
    'definition.versioned',
    'definition.retired',
    /**
     * An externally-owned compliance obligation moved — today, Form I-9 in QuickBooks Workforce.
     * Its own action rather than `step.approved`, because the interesting fact is the TRANSITION
     * (`from` → `to`), and reusing an approval action would make four different movements
     * indistinguishable in the trail.
     */
    'external-checkpoint.set',
    /** An operator handed a parked effect back to the dispatcher. Not a delivery claim. */
    'effect.resumed',
    'token.issued',
    'token.used',
    'token.failed',
    'token.locked',
    'token.revoked',
    'token.re-requested',
    'upload.received',
    'upload.rejected',
    /** #232: the person answered "I don't have this ready right now". Identifiers only. */
    'upload.marked-not-ready',
    /** #232: HR reopened that step and queued the secure upload-link email. */
    'upload.link-requested',
    /** A malware verdict was recorded against an upload. Identifiers and the verdict only. */
    'upload.scanned',
    /**
     * LEGACY — SharePoint filing (#61). No longer written since ADR-009; kept so the rows already
     * stored still read.
     */
    'file.routed',
    'file.route-parked',
    'file.route-blocked',
    /** ADR-009 §5: a file committed to, waiting for, or refused by protected storage. */
    'file.stored',
    'file.store-parked',
    'file.store-blocked',
    /** ADR-009 §5: written BEFORE a byte is read, so a failed audit write serves nothing. */
    'file.viewed',
    'file.downloaded',
    /**
     * The serve failed AFTER the row above was written (#239). `file.viewed` is written before a
     * byte is read, deliberately — a failed audit write must serve nothing — so an over-cap,
     * missing version or hash mismatch leaves a row that reads as served when nothing was. This
     * is the correction, and its absence is what made the trail say the opposite of the truth.
     */
    'file.serve-failed',
    'file.access-refused',
    /**
     * A refused request for a person's file LIST (#239). Deliberately a different action from
     * `file.access-refused`, and deliberately NOT in `fileAuditActions`.
     *
     * A refusal row is required to name the requested file's uuid as its subject, which a list
     * refusal has none of — it names no single file. The alternative was to relax that rule, and
     * that rule is why a refusal cannot leak a category. So the two refusals are two events: one
     * says "you asked for this file", the other says "you asked what files exist". The second
     * carries no subject and no file block at all, which is the most a refusal can say without
     * confirming that anything is there.
     *
     * AND THERE IS NO ACTION FOR A SERVED LIST, deliberately (#570). ADR-009 §5 sets the scope of
     * this trail as "every state change, email send, token event, file commit, and file view or
     * download", and a list is none of those: it enumerates metadata rather than disclosing a
     * document, and the content route is where a disclosure happens and is recorded. So the
     * asymmetry below is a line, not an oversight:
     *
     *   served list    -> nothing          refused list    -> `file.list-refused`
     *   served content -> `file.viewed`    refused content -> `file.access-refused`
     *
     * A refusal is recorded either way because a denial is a security signal in its own right.
     *
     * WHAT THIS LINE DOES NOT COVER, and is worth knowing before anyone leans on it: the list
     * applies `fileReadCapability` AND `requiresSensitiveRead`, so a caller holding `files.read`
     * without the sensitive capability would be served a list with restricted documents silently
     * dropped, and nothing would record it. ADR-009 §5 does not speak to partial withholding.
     *
     * NO ROLE IS SUCH A CALLER TODAY (#664). `files.read` and `people.sensitive.read` are both
     * `['HR_Admin']`, and `holdsCapability` reads nothing but roles, so anyone past the list
     * route's gate holds the sensitive capability and the list they get is whole. The withholding
     * branch is reached only by a fabricated `grants` in tests, which is the seam ADR-012 §6
     * designed; `tests/file-access.test.ts` names it and now checks the premise rather than
     * asserting it in prose.
     *
     * SO THE OPEN QUESTION IS CONDITIONAL, and #664 holds it: the day a role holds a file read
     * without the sensitive read, partial withholding becomes real and has to be answered before
     * that role ships. Note that a count cannot simply be added to a row here - `audit.read` is
     * held by HR_Viewer as well as HR_Admin, and the file reads by HR_Admin alone, so a row saying
     * "two were withheld" would tell a Viewer the thing the filter exists to keep from them.
     */
    'file.list-refused',
    /** ADR-009 §3a: the misfiling runbook (#194). */
    'file.correction-recorded',
    'file.misfiled-version-removed',
    'email.queued',
    'email.sent',
    'email.failed',
    'email.parked',
    'credential.minted',
    'verification.recorded',
    'adhoc.request-created',
    'packet.snapshotted',
    'pdf-template.published',
    /**
     * ADR-014 §2: template bytes did not hash to the pinned version's `sourceHash`. Agency scope,
     * once per (template, version, observed digest), and it carries `templateIntegrity` details
     * only — see `templateIntegrityDetailsSchema`.
     */
    'pdf-template.integrity-failed',
]);
/**
 * The partition an agency-level event lives in.
 *
 * `personId` is a uuid, so a readable sentinel like 'agency' throws at parse. The nil uuid
 * is a valid uuid that can never collide with a generated one.
 */
export const AGENCY_SCOPE_PERSON_ID = '00000000-0000-0000-0000-000000000000';
/**
 * What a `file.*` row is about (ADR-009 §5). Ids and the category only: never a file name, blob
 * name, URL or hash prefix that could identify content. `segregated` is recorded so the trail
 * reads on its own, and a row whose flag disagrees with its category does not parse.
 *
 * `code` is the CLOSED reason a `file.store-parked` or `file.store-blocked` row records (#183),
 * required on those two actions and refused on every other — see `auditEventSchema`.
 *
 * `correctionId` ties a row to the `file-correction` it carries out (#194, ADR-009 §3a): required
 * on `file.correction-recorded` and `file.misfiled-version-removed`, allowed on the `file.stored`
 * row of a re-commit, refused everywhere else. An id, never the correction's reason.
 */
export const fileAuditDetailsSchema = z
    .object({
    fileId: z.string().uuid(),
    category: fileCategorySchema,
    segregated: z.boolean(),
    /**
     * Was the file SSN-bearing (ADR-012 §4, #206)? Required on `file.viewed` and
     * `file.downloaded` and refused on every other action — see `auditEventSchema`. The proxy is
     * the only place that derives it, from the step's PINNED template, so a row that claimed it
     * anywhere else would be stating something nobody worked out.
     *
     * A `true` row is the detective control the ADR leans on: every HR user may open these files
     * today, so what has to be true is that the trail says who did. Optional rather than
     * defaulted, because absent means "nothing derived this" — a view row written before #206 is
     * silent on the question rather than asserting `false`.
     *
     * Unlike `segregated`, nothing derives it at parse time: the answer lives in another
     * document, and a row is a record of what the proxy decided at the time.
     */
    sensitive: z.boolean().optional(),
    code: fileStoreCodeSchema.optional(),
    correctionId: z.string().uuid().optional(),
})
    .strict()
    .refine((file) => file.segregated === isSegregatedCategory(file.category), {
    message: 'segregated is derived from category',
    path: ['segregated'],
});
/**
 * The actions ADR-009 §5 added. Their rows MUST carry `file` details — except
 * `file.access-refused`, which must NOT (#184) — and nothing else on them may be free text: see
 * `auditEventSchema`.
 */
export const fileAuditActions = [
    'file.stored',
    'file.store-parked',
    'file.store-blocked',
    'file.viewed',
    'file.downloaded',
    'file.serve-failed',
    'file.access-refused',
    'file.correction-recorded',
    'file.misfiled-version-removed',
];
const FILE_AUDIT_ACTIONS = new Set(fileAuditActions);
export function isFileAuditAction(action) {
    return FILE_AUDIT_ACTIONS.has(action);
}
/**
 * What a `pdf-template.integrity-failed` row records (ADR-014 §2): which pinned version, the
 * digest it should have had, and the first 12 hex characters of the digest it did have. Enough
 * to tell one tampering from another; not a full digest of bytes nobody should trust.
 */
export const templateIntegrityDetailsSchema = z
    .object({
    templateId: z.string().min(1),
    version: z.number().int().positive(),
    expectedSourceHash: digestSchema,
    observedPrefix: z.string().regex(/^[0-9a-f]{12}$/),
})
    .strict();
/**
 * What a `step.answers-viewed` row records (ADR-013 §4): WHICH form, and that it was segregated.
 *
 * `segregated` is a literal `true`, not a boolean. The row exists only for the restricted case —
 * ordinary answer views are deliberately not audited — so a `false` here would be a row that
 * should never have been written, and the schema says so rather than storing it.
 *
 * No value, no field names, no section labels: a segregated form's content is the thing this row
 * exists to protect, and an audit trail is read by more people than the form is.
 */
export const answersViewAuditDetailsSchema = z
    .object({ instanceId: z.string().min(1), segregated: z.literal(true) })
    .strict();
/** The plain object, for callers that need `.shape`. Parse documents with `auditEventSchema`. */
export const auditEventFieldsSchema = z.object({
    id: z.string().uuid(),
    personId: personIdSchema,
    docType: z.literal('audit-event'),
    action: auditActionSchema,
    /**
     * Who acted. `portal-visitor` is a field-staff member with no account (ADR-004), so
     * `actorId` is their person id rather than an Entra oid; `system` is the effect queue
     * and the nightly scan.
     *
     * Defaulted so every audit document written before Phase 2 still parses.
     */
    actorType: z.enum(['hr-user', 'portal-visitor', 'system']).default('hr-user'),
    /** Entra object id of the acting user — never their name or address. */
    actorId: z.string().min(1),
    actorRoles: z.array(z.string().min(1)).default([]),
    /** What was acted ON, when that is not the person themselves. Ids only, never content. */
    subjectType: z
        .enum(['step-instance', 'definition', 'token', 'upload', 'file', 'email', 'credential', 'packet-snapshot', 'effect', 'pdf-template'])
        .nullable()
        .default(null),
    subjectId: z.string().min(1).nullable().default(null),
    occurredAt: z.string().datetime(),
    /**
     * Field names only, never their values: "which fields changed" is auditable without
     * copying the values into a second store.
     */
    changedFields: z.array(z.string().min(1)).default([]),
    /** Set for lifecycle changes; null everywhere else. */
    from: z.string().min(1).nullable().default(null),
    to: z.string().min(1).nullable().default(null),
    reason: z.string().default(''),
    /** Required on the ADR-009 `file.*` rows (`fileAuditActions`); absent on every other row. */
    file: fileAuditDetailsSchema.optional(),
    /** Required on `pdf-template.integrity-failed`; absent on every other row. */
    templateIntegrity: templateIntegrityDetailsSchema.optional(),
    /** Required on `person.sensitive-revealed` (field and purpose code); absent on every other row. */
    sensitive: sensitiveRevealAuditDetailsSchema.optional(),
    /** Required on `step.answers-viewed`; absent on every other row. */
    answers: answersViewAuditDetailsSchema.optional(),
    /**
     * The reason CODE and the effective date of a status change (#772). Only `person.lifecycle-changed` carries it, and
     * only when the change had one; a row written before this existed has none. A code from a closed list and a calendar
     * day: no free text, no name. The note HR may add is not here.
     */
    lifecycleDetail: lifecycleAuditDetailSchema.optional(),
});
/**
 * ADR-009 §5: a file row says WHICH file (ids and category) and nothing that could name its
 * content. So on those actions the details are required, they must agree with the subject, and
 * the free-text fields (`reason`, `from`, `to`, `changedFields`) must be empty — a file name
 * written there would reach the audit trail and every export of it. A later ticket that needs
 * to say why a store parked adds a CLOSED code to `fileAuditDetailsSchema`, never free text.
 * `file` on any other action is refused too, so the two kinds of row cannot blur.
 */
/** Which file rows name a correction (#194). Anything not listed must not. */
const CORRECTION_ID_RULE = {
    'file.correction-recorded': 'required',
    'file.misfiled-version-removed': 'required',
    'file.stored': 'allowed',
};
export const auditEventSchema = auditEventFieldsSchema.superRefine((event, ctx) => {
    if ((event.action === 'pdf-template.integrity-failed') !== (event.templateIntegrity !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['templateIntegrity'],
            message: event.action === 'pdf-template.integrity-failed'
                ? 'an integrity row requires its template details'
                : `${event.action} carries no template integrity details`,
        });
    }
    /**
     * An integrity row says WHICH template failed, in ids and a digest, and nothing else — the same
     * rule the `file.*` and `person.sensitive-*` rows carry. Without it the free-text fields are the
     * one way a file name or a value could reach this row, and the trail keeps it for as long as it
     * keeps everything else.
     */
    if (event.action === 'pdf-template.integrity-failed'
        && (event.reason !== '' || event.from !== null || event.to !== null || event.changedFields.length > 0)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'an integrity row carries no free text' });
    }
    if (event.lifecycleDetail !== undefined && event.action !== 'person.lifecycle-changed') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lifecycleDetail'], message: `${event.action} carries no status detail` });
    }
    checkSensitiveAuditRow(event, ctx);
    checkAnswersViewAuditRow(event, ctx);
    if (!isFileAuditAction(event.action)) {
        if (event.file !== undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['file'], message: `${event.action} carries no file details` });
        }
        return;
    }
    if (event.action === 'file.access-refused') {
        // A refusal names the id that was asked for and nothing else (#184). Its category would
        // tell anyone who can read the trail that a segregated file exists — or that the id was
        // real at all — so it is refused here rather than left to each caller to omit.
        if (event.file !== undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['file'], message: 'a refusal carries no file details' });
        }
        if (event.subjectType !== 'file' || !z.string().uuid().safeParse(event.subjectId).success) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['subjectId'], message: 'a refusal names the requested file id as the subject' });
        }
    }
    else if (event.file === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['file'], message: `${event.action} requires file details` });
        return;
    }
    else if (event.subjectType !== 'file' || event.subjectId !== event.file.fileId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['subjectId'], message: 'a file row names its file as the subject' });
    }
    if (event.reason !== '' || event.from !== null || event.to !== null || event.changedFields.length > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'file rows carry no free text' });
    }
    // A parked row says why it waits, a blocked row why it never will, and nothing else says either.
    const codes = event.action === 'file.store-parked' ? fileStoreParkedCodeSchema : event.action === 'file.store-blocked' ? fileStoreBlockedCodeSchema : null;
    if (codes === null ? event.file?.code !== undefined : !codes.safeParse(event.file?.code).success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['file', 'code'], message: `${event.action} ${codes === null ? 'carries no reason code' : 'requires its own closed reason code'}` });
    }
    // The SSN-bearing flag belongs to a VIEW and to nothing else (ADR-012 §4, #206). Required
    // there, so the proxy cannot quietly stop stating it; refused elsewhere, so no other writer can
    // assert a verdict it never derived.
    const viewed = event.action === 'file.viewed' || event.action === 'file.downloaded';
    if (viewed !== (event.file?.sensitive !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['file', 'sensitive'],
            message: `${event.action} ${viewed ? 'requires the sensitive flag' : 'carries no sensitive flag'}`,
        });
    }
    const correction = CORRECTION_ID_RULE[event.action] ?? 'refused';
    const hasCorrection = event.file?.correctionId !== undefined;
    if ((correction === 'required' && !hasCorrection) || (correction === 'refused' && hasCorrection)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['file', 'correctionId'], message: `${event.action} ${correction === 'required' ? 'requires' : 'carries no'} correction id` });
    }
});
/**
 * ADR-012 §5: a sensitive row names the field (and, for a reveal, the closed purpose code) and
 * carries no free text, so no value — masked or not — can reach the trail through `reason`,
 * `from` or `to`. An update lists only sensitive field names; a reveal changes nothing.
 */
/**
 * ADR-013 §4: a `step.answers-viewed` row names the form and nothing else.
 *
 * The same shape as the file, integrity and sensitive rows, and for the same reason: the free-text
 * fields are the one route by which a section label or an answer could reach a trail that is
 * exported, read by more people than the form is, and kept for as long as everything else.
 */
function checkAnswersViewAuditRow(event, ctx) {
    const viewed = event.action === 'step.answers-viewed';
    const refused = event.action === 'step.answers-refused';
    if (viewed !== (event.answers !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['answers'],
            message: viewed ? 'an answers view requires its instance id' : `${event.action} carries no answers details`,
        });
    }
    if (!viewed && !refused)
        return;
    if (event.reason !== '' || event.from !== null || event.to !== null || event.changedFields.length > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'an answers row carries no free text' });
    }
    /**
     * THE SUBJECT AND THE DETAIL NAME THE SAME FORM (#386).
     *
     * The row carried `subjectId` and `answers.instanceId` as two independent fields, and nothing
     * required them to agree — so a row could name one instance as its subject and a different one in
     * its detail, which is a row nobody can read with confidence afterwards. The file rule above
     * already binds its refusal to its subject; this is the same rule for the same reason.
     *
     * A refusal has no `answers` block by design, so only its subject is checked.
     */
    if (event.subjectType !== 'step-instance' || event.subjectId === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['subjectId'], message: 'an answers row names its step instance as the subject' });
        return;
    }
    if (viewed && event.subjectId !== event.answers?.instanceId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['answers'], message: 'an answers view names the same instance as its subject' });
    }
}
function checkSensitiveAuditRow(event, ctx) {
    const updated = event.action === 'person.sensitive-updated';
    const revealed = event.action === 'person.sensitive-revealed';
    // A refusal says the same things a reveal does, minus the fact that anything was handed over.
    const refused = event.action === 'person.sensitive-reveal-refused';
    const carriesDetails = revealed || refused;
    if (carriesDetails !== (event.sensitive !== undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sensitive'], message: carriesDetails ? `${event.action} requires its field and purpose` : `${event.action} carries no sensitive details` });
    }
    if (!updated && !carriesDetails)
        return;
    if (event.reason !== '' || event.from !== null || event.to !== null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'sensitive rows carry no free text' });
    }
    const fieldsOk = updated
        ? event.changedFields.length > 0 && event.changedFields.every((f) => sensitiveFieldSchema.safeParse(f).success)
        : event.changedFields.length === 0;
    if (!fieldsOk) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['changedFields'], message: updated ? 'an update names sensitive fields only' : `a ${refused ? 'refusal' : 'reveal'} changes no fields` });
    }
}
//# sourceMappingURL=audit.js.map